import { zodResolver } from "@hookform/resolvers/zod";
import {
  PersonnummerSubmission,
  useSubmitPersonnummer,
} from "api/personnummer/useSubmitPersonnummer";
import { Button } from "components/shadcnUI/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/shadcnUI/Form";
import { Input } from "components/shadcnUI/Input";
import { useKeycloak } from "providers/KeycloakProvider";
import { Control, useFieldArray, useForm } from "react-hook-form";
import { hasPersonnummerFormat, parsePersonnummer } from "utils/personnummer";
import { z } from "zod";

// Texten är bestämd med Arvid i ADE-37 och finns bara på svenska.

const PRIVACY_POLICY_URL =
  "https://static1.squarespace.com/static/637370d847b4955402af03f2/t/6943c0b381e2fc0d0eaaae28/1766047923718/Integritetspolicy.pdf";
const personSchema = z.object({
  personnummer: z
    .string()
    .trim()
    .min(1, "Fyll i personnummer.")
    .refine(
      hasPersonnummerFormat,
      "Skriv personnumret med 10 eller 12 siffror, till exempel ÅÅÅÅMMDD-NNNN."
    )
    .refine(
      (value) => !hasPersonnummerFormat(value) || parsePersonnummer(value).ok,
      "Personnumret stämmer inte. Kontrollera siffrorna."
    ),
  firstName: z.string().trim().min(1, "Fyll i förnamn."),
  lastName: z.string().trim().min(1, "Fyll i efternamn."),
  email: z
    .string()
    .trim()
    .min(1, "Fyll i e-post.")
    .email("Skriv en giltig e-postadress."),
});

const formSchema = z.object({ persons: z.array(personSchema).min(1) });

type FormValues = z.infer<typeof formSchema>;

// Legend ligger inuti kortet tack vare float; annars ritas den på ramen.
const PERSON_CARD =
  "relative flex flex-col gap-3 rounded-lg border bg-white-100 p-4 shadow-sm";

const emptyPerson = {
  personnummer: "",
  firstName: "",
  lastName: "",
  email: "",
};

const toSubmission = (
  person: FormValues["persons"][number]
): PersonnummerSubmission => {
  const parsed = parsePersonnummer(person.personnummer);
  // Schemat har redan släppt igenom bara giltiga personnummer.
  if (!parsed.ok) throw new Error("Ogiltigt personnummer");
  return {
    personnummer: parsed.personnummer,
    firstName: person.firstName,
    lastName: person.lastName,
    email: person.email,
  };
};

interface PersonnummerFormProps {
  onLater: () => void;
  onSubmitted: () => void;
}

export const PersonnummerForm = ({
  onLater,
  onSubmitted,
}: PersonnummerFormProps) => {
  const { userProfile } = useKeycloak();
  const { submitPersonnummer, isLoading, isError } = useSubmitPersonnummer();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      persons: [
        {
          personnummer: "",
          firstName: userProfile?.firstName ?? "",
          lastName: userProfile?.lastName ?? "",
          email: userProfile?.email ?? "",
        },
      ],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "persons",
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await submitPersonnummer(values.persons.map(toSubmission));
    } catch {
      // Felet visas via isError. Det skrivs inte ut: det kan bära personnumret.
      return;
    }
    onSubmitted();
  };

  const [me, ...others] = fields;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        className="flex flex-col gap-6"
      >
        <fieldset key={me.id} className={PERSON_CARD}>
          <legend className="float-left w-full text-base font-semibold">
            Dina uppgifter
          </legend>
          <p className="text-sm text-muted-foreground">
            Vi har fyllt i namn och e-post från ditt konto. Kontrollera att de
            stämmer och ändra om något är fel.
          </p>
          <PersonFields index={0} control={form.control} />
        </fieldset>

        <section className="flex flex-col gap-3">
          <div>
            <h3 className="text-base font-semibold">
              Är ni fler som använder det här kontot?
            </h3>
            <p className="text-sm text-muted-foreground">
              Lägg till varje person som också ska kunna logga in med sitt eget
              BankID.
            </p>
          </div>
          {others.map((field, i) => {
            const index = i + 1;
            return (
              <fieldset key={field.id} className={PERSON_CARD}>
                <legend className="float-left w-full text-base font-semibold">
                  Person {index + 1}
                </legend>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-2"
                  onClick={() => remove(index)}
                >
                  Ta bort
                </Button>
                <PersonFields index={index} control={form.control} />
              </fieldset>
            );
          })}
          <Button
            type="button"
            variant="outline"
            className="self-start"
            onClick={() => append(emptyPerson)}
          >
            + Lägg till person
          </Button>
        </section>

        <div className="flex flex-col gap-2 text-sm text-muted-foreground">
          <p>
            Vi använder uppgifterna bara för att koppla BankID till rätt konto.
            Läs mer i vår{" "}
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              integritetspolicy
            </a>
            . Uppgifterna går inte att ändra när du har skickat in dem.
          </p>
          <p>
            Har du frågor, kontakta{" "}
            <a
              href="mailto:info@adecla.com"
              className="underline underline-offset-2"
            >
              info@adecla.com
            </a>
            .
          </p>
        </div>

        {isError && (
          <p role="alert" className="text-sm font-medium text-destructive">
            Något gick fel och uppgifterna skickades inte. Försök igen om en
            stund.
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onLater}>
            Senare
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="bg-secondary-100 text-white-100 hover:bg-secondary-90"
          >
            Skicka
          </Button>
        </div>
      </form>
    </Form>
  );
};

const PersonFields = ({
  index,
  control,
}: {
  index: number;
  control: Control<FormValues>;
}) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
    <FormField
      control={control}
      name={`persons.${index}.personnummer`}
      render={({ field }) => (
        <FormItem className="sm:col-span-2">
          <FormLabel>Personnummer</FormLabel>
          <FormControl>
            <Input
              {...field}
              inputMode="numeric"
              autoComplete="off"
              placeholder="ÅÅÅÅMMDD-NNNN"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
    <FormField
      control={control}
      name={`persons.${index}.firstName`}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Förnamn</FormLabel>
          <FormControl>
            <Input {...field} autoComplete="given-name" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
    <FormField
      control={control}
      name={`persons.${index}.lastName`}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Efternamn</FormLabel>
          <FormControl>
            <Input {...field} autoComplete="family-name" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
    <FormField
      control={control}
      name={`persons.${index}.email`}
      render={({ field }) => (
        <FormItem className="sm:col-span-2">
          <FormLabel>E-post</FormLabel>
          <FormControl>
            <Input {...field} type="email" autoComplete="email" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  </div>
);
